/**
 * Project entity API — TanStack Query helpers + raw service functions.
 *
 * Migrated verbatim from `features/projects/api/projects.api.ts`.
 * Behavior is identical — only the location changed. Hook/queryOptions
 * signatures and the underlying queryKey shapes are unchanged so the existing
 * TanStack cache survives the migration intact.
 *
 * Public surface (re-exported by `entities/project/index.ts`):
 *   - `projectQuery(projectId)` — queryOptions for a single project
 *   - `projectFilterOptionsQuery(workspaceId)` — queryOptions for filter options
 *   - `projectsListQuery(workspaceId, query?)` — queryOptions for paginated lists
 *   - `getProjects`, `getProjectById`, `getProjectFilterOptions`,
 *     `createDraftProject`, `updateProject`, `promoteProject`
 *     Raw service functions exposed to support **mutation composition** at
 *       call sites via `useMutation({ mutationFn })`. **Do NOT use these for
 *       reads** — always go through `projectQuery` / `projectsListQuery` /
 *       `projectFilterOptionsQuery` so caching, key invalidation, and the
 *       query-key naming contract are honored uniformly.
 *
 * Boundary note: this file imports from `@/lib/api/*` (allowed: lib) and from
 * the sibling `./query-keys` module — never from `features/`. The raw service
 * functions are deliberately part of the entity's public API; the legacy
 * `features/projects/api/projects.api.ts` shim was deleted when they were
 * exposed.
 */
import {
  DraftProjectsService,
  ProjectsService,
  type PatchApiV1ProjectsByPlatformProjectIdData,
  type PatchApiV1ProjectsDraftsByIdData,
  type ProjectFilterOptionsDto,
  type ProjectWorkspaceResolutionResponseDto,
} from '@inference.engine/workspace-api-client/web';
import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import { workspaceHttp } from '@/lib/api/http-client';
import { SUPPRESS_ERROR_NOTIFICATION_HEADER } from '@/lib/api/interceptors/problem-details.interceptor';
import type { DraftProjectDetail, ProjectDetail, ProjectsListResponse } from '../model/types';
import { PROJECTS_QUERY_KEYS, type ProjectsListQueryParams } from './query-keys';

// Query keys must never contain null/undefined slots that pretend to be valid
// workspace ids. These sentinels make disabled query states explicit and keep
// cache buckets deterministic without colliding with real workspace ids.
const MISSING_PROJECT_ID = '__missing-project-id__';
const MISSING_WORKSPACE_ID = '__missing-workspace-id__';

export const updateProject = async (
  platformProjectId: string,
  data: PatchApiV1ProjectsByPlatformProjectIdData['body'],
): Promise<ProjectDetail> => {
  const response = await ProjectsService.updateProject({
    client: workspaceHttp,
    path: {
      platformProjectId,
    },
    body: data,
    // Surface name-conflict (409) inline on the form; suppress global toast.
    // eslint-disable-next-line i18next/no-literal-string -- request header value, never rendered
    headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
  });

  if (!response.data) {
    throw new Error('Failed to update project');
  }

  return response.data;
};

/** Update a draft project during the intake phase (addressed by the internal id). */
export const updateDraft = async (
  id: string,
  data: PatchApiV1ProjectsDraftsByIdData['body'],
): Promise<DraftProjectDetail> => {
  const response = await DraftProjectsService.updateDraft({
    client: workspaceHttp,
    path: { id },
    body: data,
    // eslint-disable-next-line i18next/no-literal-string -- request header value, never rendered
    headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
  });

  if (!response.data) {
    throw new Error('Failed to update draft project');
  }

  return response.data;
};

export const deleteProject = async (platformProjectId: string): Promise<void> => {
  await ProjectsService.deleteProject({
    client: workspaceHttp,
    path: { platformProjectId },
  });
};

export const promoteProject = async (id: string): Promise<ProjectDetail> => {
  const response = await DraftProjectsService.publishDraft({
    client: workspaceHttp,
    path: {
      id,
    },
    headers: {
      // eslint-disable-next-line i18next/no-literal-string -- request header value, never rendered
      [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true',
    },
  });

  if (!response.data) {
    throw new Error('Failed to promote project');
  }

  return response.data;
};

export const createDraftProject = async (): Promise<DraftProjectDetail> => {
  const response = await DraftProjectsService.createDraft({
    client: workspaceHttp,
  });

  if (!response.data) {
    throw new Error('Failed to create draft project');
  }

  return response.data;
};

export const getProjects = async (
  query?: ProjectsListQueryParams,
): Promise<ProjectsListResponse> => {
  const response = await ProjectsService.list({
    client: workspaceHttp,
    query,
  });

  if (!response.data) {
    throw new Error('Failed to fetch projects: no data returned from workspace API');
  }

  return response.data;
};

type GetProjectByIdOptions = {
  suppressErrorNotification?: boolean;
};

export const getProjectById = async (
  platformProjectId: string,
  options?: GetProjectByIdOptions,
): Promise<ProjectDetail> => {
  const response = await ProjectsService.getProjectById({
    client: workspaceHttp,
    path: {
      platformProjectId,
    },
    // When the resolution seam is active it owns the not-found UX (in-page
    // empty state), so a cross-workspace 404 must not flash the global
    // persistent toast. Opt-in only: with the flag off (and for every other
    // consumer of this fetch) the pre-seam toast behavior is preserved.
    headers: options?.suppressErrorNotification
      ? // eslint-disable-next-line i18next/no-literal-string -- request header value, never rendered
        { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' }
      : undefined,
  });

  if (!response.data) {
    throw new Error('Failed to fetch project: no data returned from workspace API');
  }

  return response.data;
};

/**
 * Wire DTO for `GET /projects/:id/resolve-workspace`, aliased so entity consumers don't
 * couple to the generated client's naming.
 */
export type ProjectResolution = ProjectWorkspaceResolutionResponseDto;

const resolveProject = async (id: string): Promise<ProjectResolution> => {
  const response = await ProjectsService.resolveWorkspace({
    client: workspaceHttp,
    path: {
      id,
    },
    // The resolution seam maps a 404 to its own generic "not available"
    // state; the global toast would leak the raw error first.
    // eslint-disable-next-line i18next/no-literal-string -- request header value, never rendered
    headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
  });

  if (!response.data) {
    throw new Error('Failed to resolve project');
  }

  return response.data;
};

export const getProjectFilterOptions = async (): Promise<ProjectFilterOptionsDto> => {
  const response = await ProjectsService.getFilterOptions({
    client: workspaceHttp,
  });

  if (!response.data) {
    throw new Error('Failed to fetch project filter options');
  }

  return response.data;
};

export const updateProjectMembers = async (
  platformProjectId: string,
  payload: { userIds: string[]; roles?: Array<{ userId: string; roleId: string }> },
): Promise<void> => {
  await workspaceHttp.put({
    url: '/api/v1/projects/{platformProjectId}/members',
    path: { platformProjectId },
    body: payload,
    // The members panel says itself why a save was refused, naming the role it could not assign.
    headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: '403' },
  });
};

export const projectQuery = (projectId: string | undefined, options?: GetProjectByIdOptions) =>
  queryOptions({
    // `options` only tunes toast suppression on the request — the fetched data
    // is identical, so it must NOT split the cache bucket (the detail key
    // shape is pinned by the cache contract and shared across consumers).
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: PROJECTS_QUERY_KEYS.detail(projectId ?? MISSING_PROJECT_ID),
    queryFn: () => getProjectById(projectId ?? MISSING_PROJECT_ID, options),
    enabled: !!projectId,
  });

/**
 * Deep-link resolution of a project id to its owning workspace/organization.
 * Runs BEFORE the workspace-scoped `projectQuery` so ProjectDetail can switch
 * the active workspace (or show an access/not-available state) instead of
 * letting the ambient `x-selected-workspace-id` header 404 the fetch.
 */
export const projectResolutionQuery = (projectId: string | undefined) =>
  queryOptions({
    queryKey: PROJECTS_QUERY_KEYS.resolution(projectId ?? MISSING_PROJECT_ID),
    queryFn: () => resolveProject(projectId ?? MISSING_PROJECT_ID),
    enabled: !!projectId,
  });

export const projectFilterOptionsQuery = (workspaceId: string | null | undefined) =>
  queryOptions({
    queryKey: PROJECTS_QUERY_KEYS.filterOptions(workspaceId ?? MISSING_WORKSPACE_ID),
    queryFn: getProjectFilterOptions,
    enabled: !!workspaceId,
  });

/**
 * TanStack `queryOptions` for the paginated projects list.
 *
 * Replaces the ad-hoc `useQuery({ queryKey: [...] })` block previously inlined
 * in `useProjectsList`. Keeps cache safety on workspace switch (the workspaceId
 * is in the cache key per `PROJECTS_QUERY_KEYS.list`) and is automatically
 * disabled while no workspace is selected.
 *
 * Usage:
 *   const { data, isFetching } = useQuery(projectsListQuery(workspaceId, query));
 */
export const projectsListQuery = (
  workspaceId: string | null | undefined,
  query?: ProjectsListQueryParams,
) =>
  queryOptions({
    queryKey: PROJECTS_QUERY_KEYS.list(workspaceId ?? MISSING_WORKSPACE_ID, query),
    queryFn: () => getProjects(query),
    enabled: !!workspaceId,
  });

/**
 * Infinite query for projects list with pagination support.
 *
 * Returns pages of 20 projects at a time. Automatically fetches the next page
 * when the user scrolls to the bottom of the list (via IntersectionObserver).
 *
 * **Cache key:** Uses `PROJECTS_QUERY_KEYS.infinite()` which returns
 * `['projects', 'list', 'infinite', wsId, query]`. The 'infinite' discriminator
 * prevents collision with flat `projectsListQuery` calls (which omit it), while
 * the shared `['projects', 'list']` prefix ensures mutations can invalidate
 * both variants uniformly.
 *
 * The baseQuery parameter accepts all ProjectsListQueryParams EXCEPT page and
 * pageSize (those are controlled by the infinite query itself). This allows
 * sorting, filtering, and search while maintaining infinite scroll behavior.
 *
 * Usage:
 *   const { data, hasNextPage, fetchNextPage, isFetchingNextPage } =
 *     useInfiniteQuery(projectsInfiniteQuery(workspaceId, { sortBy: 'title' }));
 *
 *   const allProjects = data?.pages.flatMap(p => p.items) ?? [];
 */
export const projectsInfiniteQuery = (
  workspaceId: string | null | undefined,
  baseQuery?: Omit<ProjectsListQueryParams, 'page' | 'pageSize'>,
) =>
  infiniteQueryOptions({
    queryKey: PROJECTS_QUERY_KEYS.infinite(workspaceId ?? MISSING_WORKSPACE_ID, baseQuery),
    queryFn: ({ pageParam }) => getProjects({ ...baseQuery, page: pageParam, pageSize: 20 }),
    enabled: !!workspaceId,
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      // If last page has 20 items, there might be more pages
      if (lastPage.items.length === 20) {
        return allPages.length + 1;
      }
      // No more pages
      return undefined;
    },
  });
